/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { lodestarColors, ursaColors } from "@smartsheet/design-tokens";
import { CheckThinIcon } from "@smartsheet/lodestar-icons";
import { genId } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import { CheckmarkIcon } from "@smartsheet/ursa-icons";
import fuzzySort from "fuzzysort";
import { Ref, useCallback, useMemo } from "react";
import { I18nContextProvider } from "../../i18n/i18n";
import { useLoggingDecorator, useLoggingEnabledByDefault } from "../../util/logging";
import { useCombinedThemeMode } from "../../util/theme/useCombinedThemeMode";
import { TypeRampV2 } from "../typography";
import { SelectBasic } from "./components/select/SelectBasic";
import { SelectComboBox } from "./components/select/SelectComboBox";
import { optionFontTheme } from "./components/selectV2Theme";
import { FuzzySearchResult } from "./components/shared/FuzzySearchResult";
import { OptionBase } from "./components/shared/OptionBase";
import { injectCatalog } from "./i18n/SelectCatalog";
import { BaseOptionType, SelectProps } from "./types";
import { internalOptionToString } from "./utils";

export const selectedItemTheme = {
    icon: {
        "lodestar-light": <CheckThinIcon size="small" color={lodestarColors.visBlue} />,
        oldestar: <CheckThinIcon size="small" color={lodestarColors.visBlue} />,
        "ursa-light": <CheckmarkIcon size={20} color={ursaColors.blue500} />,
    },
};

export const Select = <Option extends BaseOptionType>({
    onChange,
    clientId,
    formatOption,
    innerRef,
    isOptionDisabled,
    isLoading = false,
    isSearchable = false,
    isReadonly = false,
    isClearable = true,
    useFuzzySearch = false,
    locale,
    maxMenuHeight = 200,
    maxOptionsUntilVirtualization = 50,
    menuId: providedMenuId,
    onCreateOption,
    optionHeight,
    optionToString = internalOptionToString,
    filterOption: providedFilterOption,
    inputValue,
    setInputValue,
    hideDropdownArrow = false,
    ...rest
}: SelectProps<Option, HTMLButtonElement | HTMLInputElement>) => {
    injectCatalog();
    const themeMode = useCombinedThemeMode();

    if (process.env.NODE_ENV !== "production") {
        const hiddenInputNameDefined = typeof rest.hiddenInputName === "string";
        const getHiddenInputValueDefined = typeof rest.getHiddenInputValue === "function";
        if (hiddenInputNameDefined !== getHiddenInputValueDefined) {
            console.warn(
                "MultiSelect: To use the hidden input, you must include both" +
                    " props: getHiddenInputValue and hiddenInputName"
            );
        }
    }

    const isCreatable = typeof onCreateOption === "function";
    const isComboBox = isSearchable || isCreatable;

    const defaultFilterOption = useCallback(
        (opt: Option, input: string) =>
            useFuzzySearch
                ? fuzzySort.single(input, optionToString(opt)) !== null
                : optionToString(opt)?.toLowerCase().startsWith(input),
        [optionToString, useFuzzySearch]
    );

    const onChangeWithLogging = useLoggingEnabledByDefault(
        useLoggingDecorator({
            controlType: "select",
            clientId,
        }),
        { actionId: LogActionId.CLICK },
        onChange
    );

    const defaultFormatOption: Required<SelectProps<Option, never>>["formatOption"] = (
        option,
        index,
        selectedIndex,
        optionSpreadProps,
        isOptionSelected,
        metadata
    ) => {
        let optionLabel: string | JSX.Element = optionToString(option);

        if (useFuzzySearch) {
            optionLabel = (
                <FuzzySearchResult inputValue={metadata.inputValue} option={option} optionToString={optionToString} />
            );
        }
        return (
            <OptionBase height={optionHeight} aria-disabled={metadata.isDisabled} {...optionSpreadProps}>
                <TypeRampV2 lineClamp={1} {...optionFontTheme.variant[themeMode]} style={{ color: "inherit" }}>
                    {optionLabel}
                </TypeRampV2>
                {isOptionSelected && (
                    <span style={{ marginLeft: "auto", paddingLeft: 8 }}>{selectedItemTheme.icon[themeMode]}</span>
                )}
            </OptionBase>
        );
    };

    const menuId = useMemo(() => providedMenuId || genId("select-menu"), [providedMenuId]);

    const sharedProps = {
        onChange: onChangeWithLogging,
        clientId,
        formatOption: formatOption || defaultFormatOption,
        isOptionDisabled: isOptionDisabled,
        isLoading,
        isReadonly,
        isClearable,
        locale,
        maxMenuHeight,
        maxOptionsUntilVirtualization,
        menuId,
        optionToString,
        optionHeight,
        filterOption: providedFilterOption || defaultFilterOption,
        hideDropdownArrow,
    };

    return (
        <I18nContextProvider locale={locale}>
            {isComboBox ? (
                <SelectComboBox
                    {...rest}
                    {...sharedProps}
                    onCreateOption={onCreateOption}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    innerRef={innerRef as Ref<HTMLInputElement>}
                />
            ) : (
                <SelectBasic {...rest} {...sharedProps} innerRef={innerRef as Ref<HTMLButtonElement>} />
            )}
        </I18nContextProvider>
    );
};

Select.displayName = "Select";
